import Vue from 'vue';
import Vuex from 'vuex';
import {RawLocation} from "vue-router";
import {VueRouter} from "vue-router/types/router";
import {AxiosResponse} from "axios";
import {Token, User} from "@/models/service/Models";
import {Helper} from "@/plugins/helper";
import {ActionsEnum} from "@/entity/ActionsEnum";
import {fonts} from "@/entity/FontsEntity";
import App from "@/App.vue";
import {menuItems} from "@/store/menuItems";
import {PaymentTypeEntity} from "@/entity/PaymentTypeEntity";
import {ProductProperties} from "@/entity/ProductEntity";
import {city} from "@/entity/CityEntity";
import {district} from "@/entity/DistrictEntity";
import {OrderStatusEntity} from "@/entity/OrderStatusEntity";
import {CargoEntity} from "@/entity/CargoEntity";
import {SiteEntity} from "@/entity/SiteEntity";
Vue.use(Vuex);

export enum UserRole{
    UNKNOWN="unknown",
    GUEST="guest",
    VERIFIED_USER="verified-user",
    PENDING_USER="pending-user",
    LIMITED_ADMIN="limited-admin",
    ADMIN="admin",
}
class breadcrumb{
    text:String = "";
    disabled: boolean = false;
    href: string = "";
    link: boolean = false;
    to: string | object = {};
}
class snackbar {
    enabled:boolean = false;
    text:String = "";
}

class CacheData {
    fonts:fonts[] = [];
    paymentTypes:PaymentTypeEntity[] = [];
    productProperties:ProductProperties[] = [];
    cities:city[] = [];
    districts:district[] = [];
    orderStatus:OrderStatusEntity[] = [];
    cargo:CargoEntity[] = [];
}

export interface AppState {
    data:CacheData;
    token: Token | null;
    user:User |null;
    layoutName:String;
    breadcrumbItems:breadcrumb[];
    role:UserRole;
    pathname:string;
    initialTarget:RawLocation|null;
    router:VueRouter |null;
    helper:Helper
    imagePath:String;
    apiUrl:String;
    siteUrl:String;
    siteProxy:String;
    selectedMenuItem:any;
    menuItems:any;
    location:string;
    snackbar:snackbar;
    /** Panelde yönetilen şubeler */
    sites:SiteEntity[];
    /** Şube listesi sunucudan en az bir kez alındı mı */
    sitesLoaded:boolean;
    /** Aktif şube — tüm API istekleri bu şube için yapılır */
    activeSiteId:number|null;
    /** Panel yerel geliştirme ortamında mı (yerel API'ye bağlı) */
    localMode:boolean;
}

/* ---------------------------------------------------------------------------
 * Tarayıcı depolaması — gizli sekme / kapalı depolamada istisna fırlatabilir;
 * panel yine de açılmalı.
 * ------------------------------------------------------------------------- */
function storageGet(storage:()=>Storage, key:string):string|null{
    try{
        return storage().getItem(key);
    }catch (e) {
        return null;
    }
}
function storageSet(storage:()=>Storage, key:string, value:string):boolean{
    try{
        storage().setItem(key, value);
        return storage().getItem(key) === value;
    }catch (e) {
        return false;
    }
}
function storageRemove(storage:()=>Storage, key:string):void{
    try{
        storage().removeItem(key);
    }catch (e) {
        // depolama kapalı: yapılacak bir şey yok
    }
}
const local = () => window.localStorage;
const session = () => window.sessionStorage;

/** Son seçilen şube (tarayıcıda kalıcı) */
const ACTIVE_SITE_KEY = "activeSiteId";
/** Şube listesinin son kopyası: sayfa açılır açılmaz doğru şube seçilebilsin */
const SITES_CACHE_KEY = "sitesCache";
/** Başlıktaki seçiciyle bu sekmede elle seçilen şube */
const MANUAL_SITE_KEY = "manualSiteId";
/** Otomatik şube düzeltmesinde sonsuz yenileme döngüsüne karşı koruma */
const AUTO_RELOAD_KEY = "siteAutoReloadId";

/* ---------------------------------------------------------------------------
 * Ortam ve API adresi
 *
 * Eskiden yalnızca hostname === "localhost" yerel sayılıyordu: geliştirme
 * paneli 127.0.0.1 ya da brainfitbuca.localhost ile açıldığında CANLI API'ye
 * bağlanıyor, yerelde denenen ayarlar canlı siteye yazılıyordu.
 * ------------------------------------------------------------------------- */
function isLocalHostname(hostname:string):boolean{
    // "localhost." gibi sondaki nokta canlı sanılıyordu
    const host = String(hostname || "").trim().toLowerCase().replace(/\.+$/, "");
    if(host === "" || host === "localhost" || /\.localhost$/.test(host)
        || /\.local$/.test(host) || /\.test$/.test(host)){
        return true;
    }
    // Canlı derleme yerel ağ IP'siyle açılınca canlı API'ye yazıyordu
    const ipv4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if(ipv4){
        const a = Number(ipv4[1]);
        const b = Number(ipv4[2]);
        return a === 127 || a === 10 || a === 0
            || (a === 172 && b >= 16 && b <= 31)
            || (a === 192 && b === 168)
            || (a === 169 && b === 254);
    }
    // IPv6 (tarayıcı köşeli parantezle ve baştaki sıfırlar atılmış verir):
    // ::1, ::, fc00::/7 (fc00–fdff), fe80::/10 (fe80–febf)
    const ipv6 = host.replace(/^\[|\]$/g, "");
    if(ipv6.indexOf(":") !== -1){
        return ipv6 === "::1" || ipv6 === "::"
            || /^f[cd][0-9a-f]{2}:/.test(ipv6)
            || /^fe[89ab][0-9a-f]:/.test(ipv6);
    }
    return false;
}
function withTrailingSlash(url:string):string{
    url = String(url || "").trim();
    return url.endsWith("/") ? url : url + "/";
}
const LOCAL_MODE:boolean = process.env.NODE_ENV !== "production" || isLocalHostname(window.location.hostname);
// Yerel API başka portta çalışıyorsa: VUE_APP_LOCAL_SITE_URL=http://localhost:8096/
const LOCAL_API_URL:string = withTrailingSlash(process.env.VUE_APP_LOCAL_SITE_URL || "http://localhost:8095/");
// ekacocukaile.com artık her istekte 410 Gone dönüyor (/admin/ dahil);
// panel oraya bağlıyken hiçbir ayar kaydedilemiyordu. İki şube aynı
// uygulamayı ve veritabanını kullanır, hangi şubenin düzenlendiğini
// X-Site-Id belirler. Alan adı değişirse derlemeden önce
// VUE_APP_SITE_URL ile verilebilir.
const PRODUCTION_API_URL:string = withTrailingSlash(process.env.VUE_APP_SITE_URL || "https://brainfitguzelbahce.com.tr/");
const SITE_URL:string = LOCAL_MODE ? LOCAL_API_URL : PRODUCTION_API_URL;

/** Yerel API'nin portu (":8096" gibi); şube adresleri aynı porta gider */
const LOCAL_API_PORT_SUFFIX:string = (() => {
    const match = LOCAL_API_URL.match(/^[a-z]+:\/\/(?:\[[^\]]*\]|[^\/:?#]+)(:\d+)?/i);
    return match && match[1] ? match[1] : "";
})();

/**
 * Alan adını adres kurmak için temizler: "https://www.x.com.tr/yol" → "www.x.com.tr".
 * www korunur; Şubeler ekranında www tercihi alan adına yazılarak belirlenir.
 */
export function cleanDomain(value:any):string{
    let host = String(value || "").trim().toLowerCase();
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
    host = host.split("/")[0].split("?")[0].split("#")[0];
    return host.replace(/\.$/, "");
}

/** Eşleştirme için alan adı: port ve "www." atılır */
function matchHost(value:any):string{
    return cleanDomain(value).replace(/:\d+$/, "").replace(/^www\./, "");
}

/**
 * Şubenin sitesinin adresi (sonunda "/").
 *  - yerel mod: http://<site_key>.localhost:<yerel API portu>/ — *.localhost
 *    canlı gibi davranır (izleme kodları basılır), yerelde denemek için.
 *  - canlı: https://<alan adı>/
 */
export function buildSiteUrl(site:SiteEntity|null, localMode:boolean):string{
    if(!site){
        return "";
    }
    if(localMode){
        const key = String(site.site_key || "").trim().toLowerCase();
        if(/^[a-z0-9-]+$/.test(key)){
            return "http://" + key + ".localhost" + LOCAL_API_PORT_SUFFIX + "/";
        }
        // Alan adı olamayacak anahtar: yerel adrese ?site= ile gidilir
        return key !== "" ? LOCAL_API_URL + "?site=" + encodeURIComponent(key) : LOCAL_API_URL;
    }
    const domain = cleanDomain(site.domain);
    return domain !== "" ? "https://" + domain + "/" : "";
}

/** Şubenin canlı adresi (yerel modda da): Search Console'a verilecek adresler için */
export function buildPublicSiteUrl(site:SiteEntity|null):string{
    return buildSiteUrl(site, false);
}

function siteById(sites:SiteEntity[], id:any):SiteEntity|undefined{
    if(id === null || id === undefined || String(id).trim() === ""){
        return undefined;
    }
    return sites.find((s:SiteEntity)=> Number(s.site_id) === Number(id));
}
function defaultSite(sites:SiteEntity[]):SiteEntity|undefined{
    return sites.find((s:SiteEntity)=> Number(s.is_default) === 1) || sites[0];
}

/**
 * Panelin açıldığı adrese göre şube: ?site=<site_key|site_id>, sonra alan adı
 * (www. ve baştaki panel./yonetim./admin. atılarak) şubenin alan adı, ek alan
 * adları ya da ilk etiketi site_key ile karşılaştırılır:
 *   brainfitbuca.localhost:8081, panel.brainfitbuca.com.tr → Buca
 */
function readSiteQuery():string{
    try{
        return String(new URLSearchParams(window.location.search).get("site") || "").trim().toLowerCase();
    }catch (e) {
        return "";
    }
}
/**
 * Sayfa açılırkenki ?site= değeri. vue-router panel içinde ilk sayfa
 * değişiminde (ör. girişten sonra ana sayfaya yönlenme) sorgu dizesini
 * siliyor; şube listesi o sırada yüklenirse ?site= kayboluyor ve başka
 * şube seçiliyordu.
 */
const INITIAL_SITE_QUERY:string = readSiteQuery();
function siteFromQuery(sites:SiteEntity[]):SiteEntity|undefined{
    const wanted = readSiteQuery() || INITIAL_SITE_QUERY;
    if(wanted === ""){
        return undefined;
    }
    return sites.find((s:SiteEntity)=> String(s.site_key || "").trim().toLowerCase() === wanted)
        || (/^\d+$/.test(wanted) ? siteById(sites, wanted) : undefined);
}
function siteFromHostname(sites:SiteEntity[]):SiteEntity|undefined{
    let host = matchHost(window.location.hostname);
    host = host.replace(/^(panel|yonetim|admin)\./, "");
    if(host === "" || host.indexOf(".") === -1){
        return undefined; // localhost, 127.0.0.1 gibi adresler şube belirtmez
    }
    const firstLabel = host.split(".")[0];
    return sites.find((s:SiteEntity)=>{
        const domains = [s.domain].concat(String(s.domain_aliases || "").split(","))
            .map(matchHost)
            .filter((d:string)=> d !== "");
        return domains.indexOf(host) !== -1;
    }) || sites.find((s:SiteEntity)=> String(s.site_key || "").trim().toLowerCase() === firstLabel);
}

/**
 * Aktif şube kararı (öncelik sırasıyla):
 *  1. adresteki ?site=
 *  2. bu sekmede başlıktaki seçiciyle yapılan seçim (sayfa yenilense de korunur)
 *  3. panelin açıldığı alan adı
 *  4. tarayıcıda saklanan son seçim
 *  5. varsayılan şube
 * Adres/alan adı eşleşmesi saklanan seçimi ezer: brainfitbuca.localhost'ta
 * açılan panel, daha önce Güzelbahçe seçilmiş olsa bile Buca'yı düzenler.
 */
export function resolveActiveSite(sites:SiteEntity[]):SiteEntity|undefined{
    if(!sites || sites.length === 0){
        return undefined;
    }
    return siteFromQuery(sites)
        || siteById(sites, storageGet(session, MANUAL_SITE_KEY))
        || siteFromHostname(sites)
        || siteById(sites, storageGet(local, ACTIVE_SITE_KEY))
        || defaultSite(sites);
}

function readSitesCache():SiteEntity[]{
    try{
        const cached = JSON.parse(storageGet(local, SITES_CACHE_KEY) || "[]");
        return Array.isArray(cached) ? cached : [];
    }catch (e) {
        return [];
    }
}

/**
 * Açılıştaki aktif şube. Önbellekteki şube listesiyle adres/alan adı kuralı
 * hemen uygulanır; böylece ilk API istekleri (X-Site-Id) doğru şubeye gider.
 */
function initialActiveSiteId():number|null{
    const resolved = resolveActiveSite(readSitesCache());
    if(resolved){
        return Number(resolved.site_id);
    }
    const stored = storageGet(local, ACTIVE_SITE_KEY);
    return stored && /^\d+$/.test(stored) ? Number(stored) : null;
}

/** Aynı anda birden fazla loadSites çağrısı tek isteğe iner */
let sitesRequest:Promise<SiteEntity[]>|null = null;

/* ---------------------------------------------------------------------------
 * Kaydedilmemiş değişiklik korumaları
 *
 * Başlıktaki şube seçici sayfayı yeniliyor; kullanıcı uyarıda "Kal" dese de
 * şube değişiyor ve Teknik SEO'daki kaydedilmemiş değerler kayboluyordu.
 * Ekranlar "kirli mi?" fonksiyonunu burada kaydeder; changeSite önce sorar.
 * ------------------------------------------------------------------------- */
export type UnsavedGuard = () => boolean;
const unsavedGuards:UnsavedGuard[] = [];
/** Kullanıcı ayrılmayı onayladı: yenilemede beforeunload ikinci kez sormasın */
let leaveConfirmed = false;

export function registerUnsavedGuard(guard:UnsavedGuard):void{
    if(unsavedGuards.indexOf(guard) === -1){
        unsavedGuards.push(guard);
    }
}
export function unregisterUnsavedGuard(guard:UnsavedGuard):void{
    const index = unsavedGuards.indexOf(guard);
    if(index !== -1){
        unsavedGuards.splice(index, 1);
    }
}
export function hasUnsavedChanges():boolean{
    return unsavedGuards.some((guard:UnsavedGuard)=>{
        try{
            return !!guard();
        }catch (e) {
            return false; // bozuk koruma şube değişimini kilitlemesin
        }
    });
}
export function isLeaveConfirmed():boolean{
    return leaveConfirmed;
}


export const store = new Vuex.Store({
    state: {
        data: new CacheData(),
        snackbar:new snackbar(),
        token: null,
        user: null,
        imagePath:SITE_URL+"images/",
        apiUrl:SITE_URL+"admin/",
        siteUrl:SITE_URL,
        siteProxy:"/site/",
        location:window.location.href,
        pathname:window.location.pathname,
        initialTarget: null,
        layoutName:"GuestLayout",
        breadcrumbItems:[],
        router: null,
        helper: new Helper(),
        role:UserRole.UNKNOWN,
        selectedMenuItem:null,
        menuItems:menuItems,
        sites:readSitesCache(),
        sitesLoaded:false,
        // Adres/alan adı kuralı ya da tarayıcıda saklanan son seçim
        activeSiteId: initialActiveSiteId(),
        localMode: LOCAL_MODE,
    },
    actions: {
        go(state,target){
            //console.log("target",target)
            if(target == null || target == undefined)
                return;
            this.dispatch("checkLayout");
            state.getters.router.push(target);
            this.state.initialTarget = target;
            //this.dispatch("breadcrumb");
        },
        goHome(state){
            //console.log("gohome");
            //this.dispatch("go",state.getters.home);
            this.dispatch("go",state.getters.home);
        },
        findAction(state){
            try{
                //if(state.getters.selectedMenuItem == null || 1 == 1){

                state.getters.menuItems.forEach((navs:any)=>{
                    navs.items.forEach((nav:any,key:number)=>{
                        //console.log(nav.route,state.getters.pathname);
                        if(state.getters.isLimitedAdmin) {
                            let permission = state.getters.user.permissions.find((x: any) => x.page == nav.action && x.get == "1"); //gosterme yetkisi varsa
                            //console.log("permission", permission);
                            if(!permission){
                                navs.items.splice(key,1);
                            }
                        }
                        if(nav.route == state.getters.pathname){
                            state.commit("setSelectedMenuItem",nav)
                            return;
                        }
                    })
                })

                //}
            }catch (e) {
                console.log(e);
            }
        },
        checkMenu(state){
          //state.getters.menuItems
        },
        navigate(state,item){
            this.commit("setSelectedMenuItem",item);
            this.dispatch("go",item.route);
        },
        login(state, user) {
            //console.log("login");
            //console.log("state",state);
            //console.log(user);
        },
        logout(state){
            localStorage.clear();
            state.commit("setUserAsGuest");
            state.dispatch("go","/login");
            //window.location.href = "/login";
            console.log("logout");
            //window.location.reload();
        },
        checkLayout(state){
            //console.log("checklayout");
            let GuestLayoutRoles = [UserRole.GUEST,UserRole.UNKNOWN,UserRole.PENDING_USER];
            let UserLayoutRoles  = [UserRole.ADMIN,UserRole.LIMITED_ADMIN];
            if(GuestLayoutRoles.find(role=>role == state.getters.userType)){
                state.commit("setLayoutName","GuestLayout");
            }
            if(UserLayoutRoles.find(role=>role == state.getters.userType)){
                state.commit("setLayoutName","UserLayout");
            }
            //console.log(state.getters.layoutName);
            this.dispatch("breadcrumb");
        },
        breadcrumb(state){
            state.commit("clearBreadcrumb");
            let routePath = "";
            state.getters.router.currentRoute.fullPath.split("/").forEach((path:string)=>{
                routePath += path;
                //console.log("path,",path)
                let bc = new breadcrumb();
                bc.href = routePath;
                bc.text = state.getters.helper.capitalize(path);
                //bc.to = state.getters.router.currentRoute.
                state.commit("pushBreadcrumb",bc);
            })
        },
        authInit(state) {
            //console.log("authInit");
            Vue.prototype.$http.get("login").then((response:AxiosResponse)=>{
                state.commit("setUser",response.data);
                //console.log("setUser",response.data);
                this.dispatch("checkLayout");
                this.dispatch("findAction");
                this.dispatch("loadSites");
                //console.log("rout",state.getters.router.currentRoute)
            }).catch((err:any)=>{
                //console.log(err);
                state.dispatch("logout");
            })
        },
        async getPaymentTypes(state){
            if(this.state.data.paymentTypes.length  > 0){
                return this.state.data.paymentTypes;
            }
           let response = await Vue.prototype.$http.get("paymentTypes");
           this.commit("setPaymentTypes",response.data);
           return response.data;
        },
        async getProductProperties(state){
            if(this.state.data.productProperties.length  > 0){
                return this.state.data.productProperties;
            }
            let response = await Vue.prototype.$http.get("productProperties");
            this.commit("setProductProperties",response.data);
            return response.data;
        },
        async getCountryCityDistrict(state){
            let response = await Vue.prototype.$http.get("countryCityDistrict");
            this.commit("setCountryCityDistrict",response.data);
        },
        async getCities(state){
            if(this.state.data.cities.length  > 0){
                return this.state.data.cities;
            }
            await this.dispatch("getCountryCityDistrict");
            return  this.state.data.cities;
        },
        async getDistricts(state){
            if(this.state.data.districts.length  > 0){
                return this.state.data.districts;
            }
            await this.dispatch("getCountryCityDistrict");
            return  this.state.data.districts;
        },
        async getOrderStatus(state){
            if(this.state.data.orderStatus.length  > 0){
                return this.state.data.orderStatus;
            }
            let response = await Vue.prototype.$http.get("orderStatus");
            this.commit("setOrderStatus",response.data);
            return response.data;
        },
        /**
         * Şube listesini yükler ve aktif şubeyi adres/alan adı kuralına göre
         * belirler (bkz. resolveActiveSite). Kayıtlı şube artık yoksa
         * varsayılan şubeye döner.
         */
        loadSites(state){
            if(sitesRequest){
                return sitesRequest;
            }
            sitesRequest = (async ():Promise<SiteEntity[]> => {
                try{
                    let response = await Vue.prototype.$http.get("sites");
                    let sites:SiteEntity[] = Array.isArray(response.data) ? response.data : [];
                    this.commit("setSites",sites);
                    if(sites.length === 0){
                        return sites;
                    }

                    const resolved = resolveActiveSite(sites);
                    if(!resolved){
                        return sites;
                    }
                    const resolvedId = Number(resolved.site_id);
                    const current = this.state.activeSiteId;
                    // Şube seçilmemiş ya da artık yoksa API varsayılan şubeyi
                    // kullanmıştır; ekrandaki veriler o şubeye aittir.
                    const currentSite = siteById(sites, current);
                    const fallback = defaultSite(sites);
                    const servedId = currentSite ? Number(currentSite.site_id) : (fallback ? Number(fallback.site_id) : null);

                    if(current === null || Number(current) !== resolvedId){
                        this.commit("setActiveSite", resolvedId);
                    }else if(storageGet(local, ACTIVE_SITE_KEY) !== String(resolvedId)){
                        // Adres/alan adıyla seçilen şube de saklanır: ?site= panel
                        // içinde sayfa değişince adresten düşüyor; saklanmazsa
                        // sayfa yenilenince eski seçime (başka şubeye) dönülüyordu.
                        storageSet(local, ACTIVE_SITE_KEY, String(resolvedId));
                    }
                    if(servedId !== null && servedId !== resolvedId){
                        // Açık ekranlar başka şubenin verisini yüklemiş olabilir;
                        // kaydedilirse yanlış şubeye yazılırdı. Sayfa bir kez
                        // yenilenir (bu sekmede aynı şube için yalnızca bir kez —
                        // depolama çalışmıyorsa hiç — ki döngüye girmesin).
                        const alreadyReloaded = storageGet(session, AUTO_RELOAD_KEY) === String(resolvedId);
                        const persisted = storageGet(local, ACTIVE_SITE_KEY) === String(resolvedId);
                        if(!alreadyReloaded && persisted && storageSet(session, AUTO_RELOAD_KEY, String(resolvedId))){
                            window.location.reload();
                        }
                    }else{
                        // Ekranlar doğru şubeyle yüklendi; koruma sıfırlanır ki
                        // (ör. çıkış/giriş sonrası) gerektiğinde yine bir kez yenilensin.
                        storageRemove(session, AUTO_RELOAD_KEY);
                    }
                    return sites;
                }catch (e) {
                    console.log("sites yüklenemedi",e);
                    return [];
                }finally {
                    this.commit("setSitesLoaded", true);
                }
            })();
            return sitesRequest.finally(()=>{
                sitesRequest = null;
            });
        },
        /**
         * Aktif şubeyi değiştirir. Ekranlardaki veriler seçili şubeye göre
         * yeniden yüklenmeli; bu yüzden mevcut görünüm tazelenir.
         * Şube değiştiyse (ya da zaten seçiliyse) true, kullanıcı vazgeçtiyse
         * false döner — seçici eski şubeye geri dönebilsin.
         */
        changeSite(state,siteId:number):boolean{
            if(Number(this.state.activeSiteId) === Number(siteId)){
                return true;
            }
            // Onay şube değişmeden önce alınır; "İptal"de hiçbir şey değişmez
            if(hasUnsavedChanges()
                && !window.confirm("Kaydedilmemiş değişiklikler var. Şube değiştirilsin mi?")){
                return false;
            }
            leaveConfirmed = true;
            this.commit("setActiveSite", Number(siteId));
            // Elle seçim bu sekmede alan adı eşleşmesinden önce gelir; yoksa
            // brainfitbuca.localhost'ta Güzelbahçe seçilince yenilemeden sonra
            // tekrar Buca'ya dönülürdü.
            storageSet(session, MANUAL_SITE_KEY, String(siteId));
            // Adreste ?site= varsa o da yeni şubeye çevrilir (en yüksek öncelik o)
            try{
                const url = new URL(window.location.href);
                if(url.searchParams.has("site")){
                    const site = siteById(this.state.sites, siteId);
                    url.searchParams.set("site", site && site.site_key ? String(site.site_key) : String(siteId));
                    window.history.replaceState(window.history.state, "", url.toString());
                }
            }catch (e) {
                console.log("adres güncellenemedi", e);
            }
            // Tüm ekranların seçili şubeye göre yeniden yüklenmesini garanti eder
            window.location.reload();
            return true;
        },
        async getCargo(state){
            if(this.state.data.cargo.length  > 0){
                return this.state.data.cargo;
            }
            let response = await Vue.prototype.$http.get("cargo");
            this.commit("setCargo",response.data);
            return response.data;
        }
    },
    mutations: {
        clearBreadcrumb(state:AppState){
          state.breadcrumbItems = [];
        },
        setPaymentTypes(state:AppState,paymentTypes:any){
            state.data.paymentTypes  = paymentTypes;
        },
        setProductProperties(state:AppState,productProperties:any){
            state.data.productProperties  = productProperties;
        },
        setCountryCityDistrict(state:AppState,items:any){
          state.data.cities  = items.cities;
          state.data.districts  = items.districts;
        },
        setOrderStatus(state:AppState,items : any){
          state.data.orderStatus =  items;
        },
        setFonts(state:AppState,fonts:fonts[]){
            state.data.fonts = fonts;
        },setCargo(state:AppState,cargo:CargoEntity[]){
            state.data.cargo = cargo;
        },
        snackbar(state:AppState, text:string){
            state.snackbar.enabled = true;
            state.snackbar.text = text;
            setTimeout(()=>{
                state.snackbar.enabled = false
            },4000)
        },
        pushBreadcrumb(state:AppState,obj:breadcrumb){
          state.breadcrumbItems.push(obj);
        },
        setLayoutName(state:AppState, layoutName:String){
            state.layoutName = layoutName;
            //console.log("Layname set",layoutName);
        },
        setUserAsGuest(state:AppState){
            window.localStorage.clear();
            state.token=null;
            state.user=null;
            state.role = UserRole.GUEST;
        },
        setSelectedMenuItem(state:AppState,item:any){
            state.selectedMenuItem = item;
        },
        setInitialTarget(state:AppState, route:RawLocation) {
            console.log("setting initial target to vuex", route);
            state.initialTarget = route;
        },
        setRouter(state:AppState, router:VueRouter) {
            state.router = router;
        },
        setUser(state:AppState, user:User) {
            state.user = user;
            //console.log("roleee",state.user.role);
            switch (state.user.role) {
                case "guest":
                    state.role = UserRole.GUEST;
                    break;
                case "admin":
                    state.role = UserRole.ADMIN;
                    break;
                case "limited-admin":
                    state.role = UserRole.LIMITED_ADMIN;
                    break;
                case UserRole.PENDING_USER: 
                    state.role = UserRole.PENDING_USER;
                    break;
                case UserRole.VERIFIED_USER:
                    state.role = UserRole.VERIFIED_USER;
                    break;
                case "unknown":
                    state.role = UserRole.UNKNOWN;
                    break;
            }
            console.log("current role is set to ",state.role);
            console.log("state user",state.user);
        },
        setSites(state:AppState, sites:SiteEntity[]) {
            state.sites = sites;
            storageSet(local, SITES_CACHE_KEY, JSON.stringify(sites));
        },
        setSitesLoaded(state:AppState, loaded:boolean) {
            state.sitesLoaded = loaded;
        },
        setActiveSite(state:AppState, siteId:number) {
            state.activeSiteId = siteId;
            storageSet(local, ACTIVE_SITE_KEY, String(siteId));
        },
        setToken(state:AppState, token:Token) {
            state.token = token;
            localStorage.setItem("token",token.token);
        },
    },
    getters: {
        userType: (state:AppState) => {
            //console.log("getter worked here", state.role);
            return state.role;
        },
        user:(state:AppState) => state.user,
        pathname:(state:AppState) => state.pathname,
        selectedMenuItem:(state:AppState) => state.selectedMenuItem,
        menuItems:(state:AppState) => state.menuItems,
        apiUrl:(state:AppState) => state.apiUrl,
        siteUrl: (state:AppState) => state.siteUrl,
        siteProxy: (state:AppState) => state.siteProxy,
        imagePath:(state:AppState) => state.imagePath,
        helper: (state:AppState) => state.helper,
        router: (state:AppState) => state.router,
        initialTarget:(state:AppState) => state.initialTarget ,
        isLimitedAdmin:(state:AppState) => state.role == UserRole.LIMITED_ADMIN,
        isGuest:(state:AppState) => state.role == UserRole.GUEST,
        isUser:(state:AppState) => state.role == UserRole.VERIFIED_USER,
        isLoggedIn:(state:AppState) => state.role == UserRole.VERIFIED_USER || state.role == UserRole.ADMIN || state.role == UserRole.PENDING_USER,
        canLogout:(state:AppState) => state.role != UserRole.GUEST && state.role != UserRole.UNKNOWN,
        home: (state:AppState) => { return "/Anasayfa"
        },
        sites: (state:AppState) => state.sites,
        activeSiteId: (state:AppState) => state.activeSiteId,
        activeSite: (state:AppState) => state.sites.find(
            (s:SiteEntity)=> Number(s.site_id) === Number(state.activeSiteId)
        ) || null,
        activeSiteName: (state:AppState) => {
            let site = state.sites.find((s:SiteEntity)=> Number(s.site_id) === Number(state.activeSiteId));
            return site ? site.name : "";
        },
        sitesLoaded: (state:AppState) => state.sitesLoaded,
        /** Panel yerel API'ye bağlı mı (yerel geliştirme) */
        isLocalMode: (state:AppState) => state.localMode,
        /** Bir şubenin site adresi: yerelde http://<site_key>.localhost:<port>/, canlıda https://<alan adı>/ */
        siteUrlFor: (state:AppState) => (site:SiteEntity|null) => buildSiteUrl(site, state.localMode),
        /** Bir şubenin canlı adresi (yerel modda da https://<alan adı>/) */
        publicSiteUrlFor: () => (site:SiteEntity|null) => buildPublicSiteUrl(site),
        /** "Siteye Git": aktif şubenin sitesi */
        activeSiteUrl: (state:AppState) => {
            let site = state.sites.find((s:SiteEntity)=> Number(s.site_id) === Number(state.activeSiteId)) || null;
            return buildSiteUrl(site, state.localMode) || state.siteUrl;
        },
    }
});
